import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, XIcon, ExternalLinkIcon } from 'lucide-react';
const VENMO_URL = 'https://venmo.com/code?user_id=2049524830830592626&created=1780534702';
const CASHAPP_URL = 'https://cash.app/$yasmine1P';
type TipPageProps = {
  open: boolean;
  onClose: () => void;
};
export function TipPage({
  open,
  onClose
}: TipPageProps) {
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-366">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.35
    }} className="fixed inset-0 z-[90] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="tip-page-title" data-id="element-367">
          {/* Opaque backdrop */}
          <div className="absolute inset-0 bg-ink" data-id="element-368" />

          {/* Decorative purple glows */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none" data-id="element-369" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-aubergine/30 rounded-full blur-3xl pointer-events-none" data-id="element-370" />

          {/* Close */}
          <button type="button" onClick={onClose} aria-label="Close" className="absolute top-6 right-6 z-10 w-11 h-11 flex items-center justify-center rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink hover:border-gold transition-all" data-id="element-371">
            <XIcon size={20} data-id="element-372" />
          </button>

          {/* Content */}
          <div className="relative min-h-full flex items-center justify-center px-4 sm:px-6 py-20" data-id="element-373">
            <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} className="w-full max-w-3xl" data-id="element-374">
              {/* Header */}
              <div className="text-center mb-10" data-id="element-375">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border-2 border-gold/40 mb-5" data-id="element-376">
                  <HeartIcon size={26} className="text-gold" data-id="element-377" />
                </div>
                <p className="text-gold font-cinzel text-xs tracking-[0.4em] mb-3" data-id="element-378">
                  WITH GRATITUDE
                </p>
                <h1 id="tip-page-title" className="font-cormorant text-bone text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-5" data-id="element-379">
                  Leave a Tip
                </h1>
                <div className="flex justify-center mb-5" data-id="element-380">
                  <div className="h-px w-16 bg-gold" data-id="element-381" />
                </div>
                <p className="text-silver-muted text-sm sm:text-base leading-relaxed max-w-xl mx-auto" data-id="element-382">
                  Thank you for your generosity. Choose your preferred method
                  below — every gesture is received with sincere appreciation.
                </p>
              </div>

              {/* Payment options */}
              <div className="grid sm:grid-cols-2 gap-5" data-id="element-383">
                {/* Venmo */}
                <a href={VENMO_URL} target="_blank" rel="noopener noreferrer" className="group relative bg-onyx border border-gold/20 p-7 transition-all duration-300 hover:border-gold/60 hover:shadow-[0_0_40px_rgba(183,148,212,0.2)] hover:-translate-y-0.5" data-id="element-384">
                  {/* Top accent line */}
                  <div className="absolute top-0 left-0 w-12 h-px bg-gold group-hover:w-full transition-all duration-500" data-id="element-385" />

                  <div className="flex items-start justify-between mb-5" data-id="element-386">
                    <div data-id="element-387">
                      <p className="text-gold font-cinzel text-[10px] tracking-[0.3em] mb-1" data-id="element-388">
                        OPTION ONE
                      </p>
                      <h2 className="font-cormorant text-bone text-3xl font-light" data-id="element-389">
                        Venmo
                      </h2>
                    </div>
                    {/* Venmo mark */}
                    <div className="w-12 h-12 rounded-lg bg-[#3D95CE] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(61,149,206,0.35)]" data-id="element-390">
                      <span className="text-white font-bold text-xl" data-id="element-391">V</span>
                    </div>
                  </div>

                  <p className="text-silver-muted text-sm leading-relaxed mb-5" data-id="element-392">
                    Tap to open Venmo and complete your tip securely.
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gold/15" data-id="element-393">
                    <span className="text-gold font-cinzel text-xs tracking-[0.2em] uppercase" data-id="element-394">
                      Open Venmo
                    </span>
                    <ExternalLinkIcon size={14} className="text-gold" data-id="element-395" />
                  </div>
                </a>

                {/* Cash App */}
                <a href={CASHAPP_URL} target="_blank" rel="noopener noreferrer" className="group relative bg-onyx border border-gold/20 p-7 transition-all duration-300 hover:border-gold/60 hover:shadow-[0_0_40px_rgba(183,148,212,0.2)] hover:-translate-y-0.5" data-id="element-396">
                  <div className="absolute top-0 left-0 w-12 h-px bg-gold group-hover:w-full transition-all duration-500" data-id="element-397" />

                  <div className="flex items-start justify-between mb-5" data-id="element-398">
                    <div data-id="element-399">
                      <p className="text-gold font-cinzel text-[10px] tracking-[0.3em] mb-1" data-id="element-400">
                        OPTION TWO
                      </p>
                      <h2 className="font-cormorant text-bone text-3xl font-light" data-id="element-401">
                        Cash App
                      </h2>
                    </div>
                    {/* Cash App mark */}
                    <div className="w-12 h-12 rounded-lg bg-[#00D632] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(0,214,50,0.35)]" data-id="element-402">
                      <span className="text-white font-bold text-xl" data-id="element-403">$</span>
                    </div>
                  </div>

                  <p className="text-silver-muted text-sm leading-relaxed mb-5" data-id="element-404">
                    Tap to open Cash App and send your tip to $yasmine1P.
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gold/15" data-id="element-405">
                    <span className="text-gold font-cinzel text-xs tracking-[0.2em] uppercase" data-id="element-406">
                      Open Cash App
                    </span>
                    <ExternalLinkIcon size={14} className="text-gold" data-id="element-407" />
                  </div>
                </a>
              </div>

              {/* Back to site */}
              <div className="text-center mt-10" data-id="element-408">
                <button type="button" onClick={onClose} className="text-silver-muted text-xs uppercase tracking-[0.25em] hover:text-gold transition-colors" data-id="element-409">
                  ← Return to Website
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}